import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutline } from '@/components/Icons/CheckCircleOutline';
import { MailRead } from '@/components/Icons/MailRead';
import { More } from '@/components/Icons/More';

const actionButton =
  'text-body-14 flex h-[42px] w-full cursor-pointer items-center gap-2 rounded-xl pr-2 pl-3 whitespace-nowrap text-foreground transition-colors hover:bg-info-surface';

const actionIcon = 'size-4 shrink-0 text-muted-foreground';

interface Props {
  canSelect: boolean;
  canMarkAllAsRead: boolean;
  onMark: () => void;
  onMarkAllRead: () => void;
  triggerClassName?: string;
}

export const MailActionsMenu: React.FC<Props> = ({
  canSelect,
  canMarkAllAsRead,
  onMark,
  onMarkAllRead,
  triggerClassName = 'size-9 rounded-3xl bg-secondary hover:bg-secondary-hover',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasActions = canSelect || canMarkAllAsRead;

  const selectMark = () => {
    setIsOpen(false);
    onMark();
  };

  const selectMarkAllRead = () => {
    setIsOpen(false);
    onMarkAllRead();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!hasActions) {
    return null;
  }

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        aria-label={t('mail.mark')}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex cursor-pointer items-center justify-center text-primary transition-colors ${triggerClassName}`}
      >
        <More className="size-6" />
      </button>

      {isOpen && (
        <div
          aria-hidden
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-50 bg-black/10 animate-fade-in"
        />
      )}

      {isOpen && (
        <div className="absolute top-full right-0 z-[60] mt-1 flex w-fit origin-top-right flex-col gap-1 rounded-[14px] border border-transparent bg-popover p-2 shadow-[0px_3px_8px_rgba(18,31,55,0.08)] contrast:border-border animate-zoom-in">
          {canSelect && (
            <button type="button" onClick={selectMark} className={actionButton}>
              <CheckCircleOutline className={actionIcon} />
              {t('mail.mark')}
            </button>
          )}

          {canMarkAllAsRead && (
            <button
              type="button"
              onClick={selectMarkAllRead}
              className={actionButton}
            >
              <MailRead className={actionIcon} />
              {t('mail.markAllAsRead')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
